// ============================================
//⚔️ API VAN HELSIN FRONTEND - PRODUÇÃO
// Totalmente integrada ao backend no pod Vast.ai via Cloudflare
// ============================================

import {
  getAboutAuthToken,
} from "./aboutAuth";

export {
  consumeAboutTokenFromUrl,
  getAboutAccessStatus,
  getAboutAuthToken,
  hasAboutFullAiAccess,
  isAboutAuthEnabled,
  bootstrapAboutAuthFromUrl,
  exchangeAboutHandoff,
  fetchAboutAuthStatus,
} from "./aboutAuth";

export const API_BASE_URL =
  process.env.REACT_APP_API_BASE || "https://api.universevh.org"; // 🔹 URL pública do backend
/** Path único para processar_json (evitar concatenação ad-hoc). Nunca cachear POST. */
export const API_PATH_PROCESSAR_JSON = "/processar_json";

export const CONVERSATIONS_UI_ENABLED =
  String(process.env.REACT_APP_VH_CONVERSATIONS_ENABLED || "0") === "1";
export const CONVERSATIONS_DELETE_UI_ENABLED =
  String(process.env.REACT_APP_VH_CONVERSATIONS_DELETE_ENABLED || "0") === "1";
export const CHATROOMS_UI_ENABLED =
  String(process.env.REACT_APP_VH_CHATROOM_MODE_V1 || "1") === "1";
/** Se false, ao abrir o app inicia com nova conversa vazia e não lista histórico automaticamente. */
export const CONVERSATIONS_AUTO_LOAD_ON_OPEN =
  String(process.env.REACT_APP_VH_CONVERSATIONS_AUTO_LOAD || "0") === "1";

const VISUAL_MODE_CANONICAL = new Set(["DEFAULT", "PARABLE", "TENSION", "CRITICAL"]);

const UID_STORAGE_KEY = "vh_user_id";
const EMAIL_STORAGE_KEY = "vanhelsin_email";
const TOKEN_STORAGE_KEY = "vanhelsin_token";
const CHATROOM_UID_STORAGE_KEY = "vh_chatroom_user_id";

function normalizeEmail(email) {
  return String(email || "").trim().toLowerCase();
}

function fnv1a32(text, seed) {
  let hash = seed >>> 0;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619) >>> 0;
  }
  return hash >>> 0;
}

function deterministicUuidV4FromText(text) {
  const source = String(text || "");
  const seeds = [0x811c9dc5, 0x9e3779b9, 0x85ebca6b, 0xc2b2ae35];
  const bytes = [];

  for (let i = 0; i < seeds.length; i += 1) {
    const h = fnv1a32(`${source}|${i}`, seeds[i]);
    bytes.push((h >>> 24) & 0xff, (h >>> 16) & 0xff, (h >>> 8) & 0xff, h & 0xff);
  }

  // UUID v4: ajustar version/variant mantendo determinismo.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = bytes.map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}

function safeGet(storage, key) {
  try {
    return storage.getItem(key) || "";
  } catch {
    return "";
  }
}

function safeSet(storage, key, value) {
  try {
    storage.setItem(key, value);
  } catch {}
}

function safeRemove(storage, key) {
  try {
    storage.removeItem(key);
  } catch {}
}

function readSessionScoped(key) {
  const sessionVal = safeGet(sessionStorage, key);
  if (sessionVal) return sessionVal;
  const legacyLocal = safeGet(localStorage, key);
  if (legacyLocal) {
    safeSet(sessionStorage, key, legacyLocal);
    safeRemove(localStorage, key);
  }
  return legacyLocal;
}

function writeSessionScoped(key, value) {
  if (!value) return;
  safeSet(sessionStorage, key, value);
  safeRemove(localStorage, key);
}

function clearScoped(key) {
  safeRemove(sessionStorage, key);
  safeRemove(localStorage, key);
}

function getUserId() {
  return readSessionScoped(UID_STORAGE_KEY);
}

function setUserId(uid) {
  if (uid && typeof uid === "string") writeSessionScoped(UID_STORAGE_KEY, uid);
}

export function bindUserIdentityToEmail(email) {
  const normalized = normalizeEmail(email);
  if (!normalized) return "";
  const uid = deterministicUuidV4FromText(`vh-email:${normalized}`);
  writeSessionScoped(EMAIL_STORAGE_KEY, normalized);
  setUserId(uid);
  return uid;
}

export function getBoundUserId() {
  return getUserId();
}

function getChatroomUserId() {
  const bound = getUserId();
  if (bound) return bound;

  const existing = readSessionScoped(CHATROOM_UID_STORAGE_KEY);
  if (existing) return existing;

  const email = getBoundEmail();
  const fallback = email
    ? deterministicUuidV4FromText(`vh-chatroom:${email}`)
    : deterministicUuidV4FromText(
        `vh-chatroom:guest:${Date.now()}:${Math.random().toString(36).slice(2)}`
      );
  writeSessionScoped(CHATROOM_UID_STORAGE_KEY, fallback);
  return fallback;
}

export function getBoundChatroomUserId() {
  return getChatroomUserId();
}

export function getBoundEmail() {
  return normalizeEmail(readSessionScoped(EMAIL_STORAGE_KEY));
}

export function setSessionToken(token) {
  if (!token || typeof token !== "string") return;
  writeSessionScoped(TOKEN_STORAGE_KEY, token);
}

export function clearStoredIdentity() {
  clearScoped(EMAIL_STORAGE_KEY);
  clearScoped(TOKEN_STORAGE_KEY);
  clearScoped(UID_STORAGE_KEY);
  clearScoped(CHATROOM_UID_STORAGE_KEY);
}

function setUserIdFromResponse(response) {
  try {
    const uid = response.headers.get("X-VH-User-ID") || "";
    if (uid) setUserId(uid);
  } catch {}
}

function getLanguageHeader() {
  try {
    const fromUi = safeGet(sessionStorage, "vh_ui_lang").trim();
    if (fromUi && /^(pt|en|es)(-|$)/i.test(fromUi)) {
      if (fromUi.startsWith("pt")) return "pt-BR";
      if (fromUi.startsWith("es")) return "es-ES";
      return "en-US";
    }
    const nav = (navigator.language || "").trim();
    return nav || "pt-BR";
  } catch {
    return "pt-BR";
  }
}

function getUiLangSignal() {
  const fromUi = safeGet(sessionStorage, "vh_ui_lang").trim();
  if (fromUi) return fromUi;
  return getLanguageHeader();
}

function getCountryHeader() {
  try {
    const lang = String(navigator.language || "").trim();
    const match = lang.match(/-([A-Za-z]{2})$/);
    return match ? String(match[1] || "").toUpperCase() : "";
  } catch {
    return "";
  }
}

async function apiFetch(path, options = {}) {
  const aboutToken = getAboutAuthToken();
  const legacyToken = readSessionScoped(TOKEN_STORAGE_KEY);
  const token = aboutToken || legacyToken || "no-token";
  const uid = getUserId();
  const email = getBoundEmail();

  const headers = {
    ...(options.headers || {}),
    Authorization: `Bearer ${token}`,
    "X-VH-Language": getLanguageHeader(),
  };
  const country = getCountryHeader();
  if (country && !headers["X-VH-Country"] && !headers["x-vh-country"]) {
    headers["X-VH-Country"] = country;
  }
  
  // PATCH: enviar idioma da UI como sinal (fallback, nao como verdade)
  const uiLang = getUiLangSignal();
  if (uiLang) {
    headers["X-VH-UILang"] = uiLang;
  }

  // SEMPRE enviar user_id no header (fallback se cookie falhar)
  if (uid) {
    headers["X-VH-User-ID"] = uid;
  }
  if (email) {
    headers["X-VH-User-Email"] = email;
  }

  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers,
      credentials: "include", // Enviar cookies (vh_uid)
      cache: options.method === "POST" || options.method === "PUT" ? "no-store" : options.cache,
    });

    setUserIdFromResponse(response);
    return response;
  } catch (error) {
    if (error && error.name === "AbortError") throw error;
    console.error("[API-ERROR] Erro ao fazer requisição:", error);
    throw error;
  }
}

// 🔹 Função principal de envio de mensagens
export async function sendMessageToAPI(text, mode, options = {}) {
  try {
    const tipo = mode === "heroi" ? "pop" : "financeiro";
    const uid = getUserId();
    const conversationId = String(options?.conversationId || "").trim();
    const sessionTurns = Array.isArray(options?.session_turns) ? options.session_turns : [];

    console.log(`[API] Enviando mensagem para: ${API_BASE_URL}${API_PATH_PROCESSAR_JSON}`);

    const payload = {
      texto: text,
      tipo,
      user_id: uid || "",
      ui_lang: getUiLangSignal(),
      vh_rachel_enabled: options?.vh_rachel_enabled === true,
    };
    if (conversationId) {
      payload.conversation_id = conversationId;
    }
    if (sessionTurns.length > 0) {
      payload.session_turns = sessionTurns;
    }
    if (String(options?.response_style_bias || "").trim() === "direct_preferred") {
      payload.response_style_bias = "direct_preferred";
    }

    const reqHeaders = { "Content-Type": "application/json" };
    const optRequestId = String(options?.requestId || "").trim();
    if (optRequestId) {
      reqHeaders["X-Request-ID"] = optRequestId;
    }

    const response = await apiFetch(API_PATH_PROCESSAR_JSON, {
      method: "POST",
      headers: reqHeaders,
      body: JSON.stringify(payload),
      signal: options.signal,
      cache: "no-store",
    });

    console.log(`[API] Status da resposta: ${response.status}`);

    const parseResponseBody = async () => {
      const contentType = (response.headers.get("content-type") || "").toLowerCase();
      if (contentType.includes("application/json")) {
        return await response.json();
      }
      const bodyText = await response.text();
      return { resposta: bodyText || "" };
    };

    if (!response.ok) {
      // 402 = paywall hard-limit
      const body = await parseResponseBody().catch(() => ({}));
      const bodyText =
        (typeof body?.resposta === "string" && body.resposta) ||
        (typeof body?.message === "string" && body.message) ||
        (typeof body?.detail === "string" && body.detail) ||
        "";
      if (response.status === 402) {
        return { ok: false, status: 402, text: bodyText || "Limite grátis atingido." };
      }
      if (response.status === 403 || response.status === 451) {
        return {
          ok: false,
          status: response.status,
          text: bodyText || "Servico indisponivel no momento.",
          error: typeof body?.error === "string" ? body.error : "",
          terms: body?.terms || null,
          country: typeof body?.country === "string" ? body.country : "",
        };
      }
      return {
        ok: false,
        status: response.status,
        text: bodyText || `❌ Erro do backend (${response.status}).`,
      };
    }

    const body = await parseResponseBody();
    const replyText =
      (typeof body?.resposta === "string" && body.resposta) ||
      (typeof body === "string" && body) ||
      "";
    const out = {
      ok: true,
      status: 200,
      text: replyText || "❌ Erro: resposta vazia do Oráculo.",
      usedParable: body?.used_parable === true,
      assistantMessageId:
        body && typeof body.assistant_message_id === "string" ? body.assistant_message_id : "",
      audiobookOffer:
        body && typeof body.audiobook_offer === "object" ? body.audiobook_offer : null,
      conversationId:
        body && typeof body.conversation_id === "string" ? body.conversation_id : conversationId,
    };
    const visualModeRaw =
      body && typeof body.visual_mode === "string"
        ? body.visual_mode
        : body && typeof body.visualMode === "string"
          ? body.visualMode
          : "";
    const visualMode = String(visualModeRaw || "").trim().toUpperCase();
    if (VISUAL_MODE_CANONICAL.has(visualMode)) {
      out.visualMode = visualMode;
    }
    if (body && Object.prototype.hasOwnProperty.call(body, "visual_intensity")) {
      out.visualIntensity = body.visual_intensity;
    }
    return out;
  } catch (err) {
    if (err && err.name === "AbortError") {
      return {
        ok: false,
        status: 499,
        aborted: true,
        text: "⏹️ Geração interrompida.",
      };
    }

    console.error("⚠️ Erro ao conectar com o backend:", err);
    console.error("⚠️ URL do backend:", API_BASE_URL);
    console.error("⚠️ Detalhes do erro:", err.message);
    
    return {
      ok: false,
      status: 0,
      text: `❌ O Oráculo está em silêncio (erro de conexão com ${API_BASE_URL}${API_PATH_PROCESSAR_JSON}). Verifique se o backend está online.`,
    };
  }
}

export async function listConversations({ limit = 50, cursor = "" } = {}) {
  const lim = Number.isFinite(Number(limit)) ? Math.max(1, Math.min(200, parseInt(limit, 10))) : 50;
  const cur = String(cursor || "").trim();
  const query = cur ? `?limit=${lim}&cursor=${encodeURIComponent(cur)}` : `?limit=${lim}`;
  const response = await apiFetch(`/api/conversations${query}`, { method: "GET" });
  if (!response.ok) throw new Error(`api/conversations list: ${response.status}`);
  return await response.json();
}

export async function createConversation(payload = {}) {
  const body = {};
  if (payload && typeof payload === "object") {
    if (typeof payload.title === "string") body.title = payload.title;
    if (typeof payload.first_user_message === "string") {
      body.first_user_message = payload.first_user_message;
    }
  }
  const response = await apiFetch(`/api/conversations`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`api/conversations create: ${response.status}`);
  return await response.json();
}

export async function getConversation(conversationId) {
  const cid = encodeURIComponent(String(conversationId || "").trim());
  const response = await apiFetch(`/api/conversations/${cid}`, { method: "GET" });
  if (!response.ok) throw new Error(`api/conversations get: ${response.status}`);
  return await response.json();
}

export async function getConversationMessages(conversationId, { limit = 50, before = "" } = {}) {
  const cid = encodeURIComponent(String(conversationId || "").trim());
  const lim = Number.isFinite(Number(limit)) ? Math.max(1, Math.min(200, parseInt(limit, 10))) : 50;
  const prev = String(before || "").trim();
  const query = prev ? `?limit=${lim}&before=${encodeURIComponent(prev)}` : `?limit=${lim}`;
  const response = await apiFetch(`/api/conversations/${cid}/messages${query}`, { method: "GET" });
  if (!response.ok) throw new Error(`api/conversations messages: ${response.status}`);
  return await response.json();
}

export async function deleteConversation(conversationId) {
  const cid = encodeURIComponent(String(conversationId || "").trim());
  const response = await apiFetch(`/api/conversations/${cid}`, { method: "DELETE" });
  if (!response.ok) throw new Error(`api/conversations delete: ${response.status}`);
  return await response.json();
}

export async function deleteConversationMessages(conversationId, messageIds = []) {
  const cid = encodeURIComponent(String(conversationId || "").trim());
  const ids = Array.isArray(messageIds)
    ? messageIds.map((x) => String(x || "").trim()).filter(Boolean)
    : [];
  const response = await apiFetch(`/api/conversations/${cid}/messages`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ message_ids: ids }),
  });
  if (!response.ok) throw new Error(`api/conversations delete messages: ${response.status}`);
  return await response.json();
}

export async function deleteAllConversations() {
  const response = await apiFetch(`/api/conversations`, { method: "DELETE" });
  if (!response.ok) throw new Error(`api/conversations delete all: ${response.status}`);
  return await response.json();
}

export async function getParableShortConclusion(payload = {}) {
  const body = {
    assistant_message_id: String(payload?.assistant_message_id || "").trim(),
    conversation_id: String(payload?.conversation_id || "").trim(),
    ui_lang: getUiLangSignal(),
  };
  const response = await apiFetch(`/api/parable-actions/short-conclusion`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return await parseJsonOrThrow(response, "api/parable-actions short-conclusion");
}

async function parseJsonOrThrow(response, label) {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail =
      (typeof body?.detail === "string" && body.detail) ||
      (typeof body?.message === "string" && body.message) ||
      "";
    throw new Error(`${label}: ${response.status}${detail ? ` ${detail}` : ""}`);
  }
  return body;
}

export async function listChatRooms() {
  const response = await apiFetch(`/api/chatrooms`, {
    method: "GET",
    headers: { "X-VH-User-ID": getChatroomUserId() },
  });
  return await parseJsonOrThrow(response, "api/chatrooms list");
}

export async function getChatRoom(roomId) {
  const rid = encodeURIComponent(String(roomId || "").trim());
  const response = await apiFetch(`/api/chatrooms/${rid}`, {
    method: "GET",
    headers: { "X-VH-User-ID": getChatroomUserId() },
  });
  return await parseJsonOrThrow(response, "api/chatrooms get");
}

export async function createChatRoom(payload = {}) {
  const body = {
    title: String(payload?.title || "").trim(),
    max_participants: Number.isFinite(Number(payload?.max_participants))
      ? Math.max(2, Math.min(50, parseInt(payload.max_participants, 10)))
      : 8,
    policy_json:
      payload?.policy_json && typeof payload.policy_json === "object"
        ? payload.policy_json
        : {},
    display_name: String(payload?.display_name || "").trim(),
  };
  const response = await apiFetch(`/api/chatrooms`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-VH-User-ID": getChatroomUserId(),
    },
    body: JSON.stringify(body),
  });
  return await parseJsonOrThrow(response, "api/chatrooms create");
}

export async function createChatRoomInvite(roomId, payload = {}) {
  const rid = encodeURIComponent(String(roomId || "").trim());
  const body = {
    expires_in_hours: Number.isFinite(Number(payload?.expires_in_hours))
      ? Math.max(1, Math.min(168, parseInt(payload.expires_in_hours, 10)))
      : 24,
    max_uses: Number.isFinite(Number(payload?.max_uses))
      ? Math.max(1, Math.min(100, parseInt(payload.max_uses, 10)))
      : 1,
  };
  const response = await apiFetch(`/api/chatrooms/${rid}/invite`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-VH-User-ID": getChatroomUserId(),
    },
    body: JSON.stringify(body),
  });
  return await parseJsonOrThrow(response, "api/chatrooms invite");
}

export async function revokeChatRoomInvites(roomId) {
  const rid = encodeURIComponent(String(roomId || "").trim());
  const response = await apiFetch(`/api/chatrooms/${rid}/invite/revoke_all`, {
    method: "POST",
    headers: { "X-VH-User-ID": getChatroomUserId() },
  });
  return await parseJsonOrThrow(response, "api/chatrooms revoke");
}

export async function joinChatRoom(payload = {}) {
  const response = await apiFetch(`/api/chatrooms/join`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-VH-User-ID": getChatroomUserId(),
    },
    body: JSON.stringify({
      token: String(payload?.token || "").trim(),
      display_name: String(payload?.display_name || "").trim(),
    }),
  });
  return await parseJsonOrThrow(response, "api/chatrooms join");
}

export async function getChatRoomMessages(roomId, { limit = 200 } = {}) {
  const rid = encodeURIComponent(String(roomId || "").trim());
  const lim = Number.isFinite(Number(limit))
    ? Math.max(1, Math.min(200, parseInt(limit, 10)))
    : 200;
  const response = await apiFetch(`/api/chatrooms/${rid}/messages?limit=${lim}`, {
    method: "GET",
    headers: { "X-VH-User-ID": getChatroomUserId() },
  });
  return await parseJsonOrThrow(response, "api/chatrooms messages");
}

export async function getChatRoomMemory(roomId) {
  const rid = encodeURIComponent(String(roomId || "").trim());
  const response = await apiFetch(`/api/chatrooms/${rid}/memory`, {
    method: "GET",
    headers: { "X-VH-User-ID": getChatroomUserId() },
  });
  return await parseJsonOrThrow(response, "api/chatrooms memory");
}

export async function rebuildChatRoomMemory(roomId) {
  const rid = encodeURIComponent(String(roomId || "").trim());
  const response = await apiFetch(`/api/chatrooms/${rid}/memory/rebuild`, {
    method: "POST",
    headers: { "X-VH-User-ID": getChatroomUserId() },
  });
  return await parseJsonOrThrow(response, "api/chatrooms memory rebuild");
}

export async function clearChatRoomConversation(roomId) {
  const rid = encodeURIComponent(String(roomId || "").trim());
  const response = await apiFetch(`/api/chatrooms/${rid}/conversation/clear`, {
    method: "POST",
    headers: { "X-VH-User-ID": getChatroomUserId() },
  });
  return await parseJsonOrThrow(response, "api/chatrooms conversation clear");
}

export async function deleteChatRoomMessages(roomId, messageIds = []) {
  const rid = encodeURIComponent(String(roomId || "").trim());
  const ids = Array.isArray(messageIds)
    ? messageIds.map((item) => String(item || "").trim()).filter(Boolean)
    : [];
  const response = await apiFetch(`/api/chatrooms/${rid}/messages/delete`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-VH-User-ID": getChatroomUserId(),
    },
    body: JSON.stringify({ message_ids: ids }),
  });
  return await parseJsonOrThrow(response, "api/chatrooms messages delete");
}

export async function postChatRoomMessage(roomId, payload = {}) {
  const rid = encodeURIComponent(String(roomId || "").trim());
  const response = await apiFetch(`/api/chatrooms/${rid}/message`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-VH-User-ID": getChatroomUserId(),
    },
    body: JSON.stringify({
      text: String(payload?.text || ""),
      reply_to: String(payload?.reply_to || "").trim(),
      display_name: String(payload?.display_name || "").trim(),
    }),
  });
  return await parseJsonOrThrow(response, "api/chatrooms message");
}

export async function analyzeChatRoom(roomId, payload = {}) {
  const rid = encodeURIComponent(String(roomId || "").trim());
  const response = await apiFetch(`/api/chatrooms/${rid}/analyze`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-VH-User-ID": getChatroomUserId(),
    },
    body: JSON.stringify({
      lens: String(payload?.lens || "heroi").trim(),
      scope_type: String(payload?.scope_type || "last_n").trim(),
      scope_value: Number.isFinite(Number(payload?.scope_value))
        ? Math.max(1, Math.min(20, parseInt(payload.scope_value, 10)))
        : 20,
      question: String(payload?.question || "").trim(),
      request_id: String(payload?.request_id || "").trim(),
    }),
  });
  return await parseJsonOrThrow(response, "api/chatrooms analyze");
}

export async function bootstrapSession() {
  const response = await apiFetch(`/bootstrap`, { method: "GET" });
  const contentType = (response.headers.get("content-type") || "").toLowerCase();
  const body = contentType.includes("application/json")
    ? await response.json().catch(() => ({}))
    : { message: await response.text().catch(() => "") };
  if (!response.ok) {
    return {
      ok: false,
      status: response.status,
      error: typeof body?.error === "string" ? body.error : "",
      message: typeof body?.message === "string" ? body.message : "",
      country: typeof body?.country === "string" ? body.country : "",
      terms: body?.terms || null,
    };
  }
  if (body && typeof body.user_id === "string" && body.user_id.trim()) {
    setUserId(body.user_id.trim());
  }
  return { ok: true, status: response.status, data: body };
}

export async function getDragaoStatus(conversationId) {
  const cid = String(conversationId || "").trim();
  const query = cid ? `?conversation_id=${encodeURIComponent(cid)}` : "";
  const response = await apiFetch(`/mode/dragao/status${query}`, { method: "GET" });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    return {
      ok: false,
      status: response.status,
      error: typeof body?.error === "string" ? body.error : "",
      detail: typeof body?.detail === "string" ? body.detail : "",
      terms: body?.terms || null,
    };
  }
  return { ok: true, status: response.status, ...body };
}

export async function acceptDragaoMode({ conversationId, ageConfirmed, termsAccepted }) {
  const response = await apiFetch(`/mode/dragao/accept`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      conversation_id: String(conversationId || "").trim(),
      age_confirmed: !!ageConfirmed,
      terms_accepted: !!termsAccepted,
    }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    return {
      ok: false,
      status: response.status,
      error: typeof body?.error === "string" ? body.error : "",
      detail: typeof body?.detail === "string" ? body.detail : "",
      daily_limit_reached: body?.daily_limit_reached === true,
      state: body?.state || null,
      questions: body?.questions || null,
    };
  }
  return { ok: true, status: response.status, ...body };
}

export async function disableDragaoMode(conversationId) {
  const response = await apiFetch(`/mode/dragao/disable`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ conversation_id: String(conversationId || "").trim() }),
  });
  if (!response.ok) throw new Error(`mode/dragao/disable: ${response.status}`);
  return await response.json();
}

export async function identifyDragao(payload = {}) {
  const response = await apiFetch(`/mode/dragao/identify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    return {
      ok: false,
      status: response.status,
      error: typeof body?.error === "string" ? body.error : "",
      detail: typeof body?.detail === "string" ? body.detail : "",
      state: body?.state || null,
      questions: body?.questions || null,
    };
  }
  return { ok: true, status: response.status, ...body };
}

export async function submitDragaoBaseline(payload = {}) {
  const response = await apiFetch(`/mode/dragao/baseline`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    return {
      ok: false,
      status: response.status,
      error: typeof body?.error === "string" ? body.error : "",
      detail: typeof body?.detail === "string" ? body.detail : "",
      state: body?.state || null,
      questions: body?.questions || null,
    };
  }
  return { ok: true, status: response.status, ...body };
}

export async function submitDragaoCheckin(payload = {}) {
  const response = await apiFetch(`/mode/dragao/checkin`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    return {
      ok: false,
      status: response.status,
      error: typeof body?.error === "string" ? body.error : "",
      detail: typeof body?.detail === "string" ? body.detail : "",
      state: body?.state || null,
      questions: body?.questions || null,
    };
  }
  return { ok: true, status: response.status, ...body };
}

export async function finalizeDragaoReview(payload = {}) {
  const response = await apiFetch(`/mode/dragao/review`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    return {
      ok: false,
      status: response.status,
      error: typeof body?.error === "string" ? body.error : "",
      detail: typeof body?.detail === "string" ? body.detail : "",
      state: body?.state || null,
      questions: body?.questions || null,
    };
  }
  return { ok: true, status: response.status, ...body };
}

export async function getTermsStatus() {
  const response = await apiFetch(`/terms/status`, { method: "GET" });
  if (!response.ok) throw new Error(`terms/status: ${response.status}`);
  return await response.json();
}

export async function acceptTerms(termsVersion) {
  const response = await apiFetch(`/terms/accept`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      accept: true,
      terms_version: String(termsVersion || "").trim(),
    }),
  });
  if (!response.ok) throw new Error(`terms/accept: ${response.status}`);
  return await response.json();
}

export async function dismissAudiobookOffer(audioId) {
  const response = await apiFetch(`/api/audiobook/dismiss`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      audio_id: String(audioId || "").trim(),
    }),
  });
  if (!response.ok) throw new Error(`api/audiobook/dismiss: ${response.status}`);
  return await response.json();
}

export async function acceptAudiobookOffer(offerId, audioId, lang) {
  const response = await apiFetch(`/api/audiobook/accept`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      offer_id: String(offerId || "").trim(),
      audio_id: String(audioId || "").trim(),
      lang: String(lang || "").trim().toLowerCase(),
    }),
  });
  if (!response.ok) throw new Error(`api/audiobook/accept: ${response.status}`);
  return await response.json();
}

export async function getBillingStatus() {
  const response = await apiFetch(`/billing/status`, { method: "GET" });
  if (!response.ok) throw new Error(`billing/status: ${response.status}`);
  return await response.json();
}

export async function createCheckout(interval = "month", options = {}) {
  const cycle = String(interval || "").toLowerCase() === "year" ? "year" : "month";
  const country = getCountryHeader();
  const headers = { "Content-Type": "application/json" };
  if (country) headers["X-VH-Country"] = country;

  const body = { interval: cycle };
  if (options.termsAccepted === true) body.terms_accepted = true;
  if (options.euWithdrawalConsent === true) body.eu_withdrawal_consent = true;

  const response = await apiFetch(`/billing/checkout`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`billing/checkout: ${response.status}`);
  return await response.json(); // { url }
}

function normalizeMemoryNamespace(namespace) {
  const ns = String(namespace || "").trim().toLowerCase();
  return ns === "dragao" ? "dragao" : "normal";
}

function memoryNsQuery(namespace) {
  return `?ns=${encodeURIComponent(normalizeMemoryNamespace(namespace))}`;
}

export async function getMemoryStatus(namespace = "normal") {
  const response = await apiFetch(`/memory/status${memoryNsQuery(namespace)}`, { method: "GET" });
  if (!response.ok) throw new Error(`memory/status: ${response.status}`);
  return await response.json();
}

export async function setMemoryEnabled(enabled, namespace = "normal") {
  const ns = normalizeMemoryNamespace(namespace);
  const response = await apiFetch(`/memory/settings`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ enabled: !!enabled, namespace: ns }),
  });
  if (!response.ok) throw new Error(`memory/settings: ${response.status}`);
  return await response.json();
}

export async function deleteMemorySummaries(namespace = "normal") {
  const ns = normalizeMemoryNamespace(namespace);
  const response = await apiFetch(`/memory/delete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ target: "summaries", namespace: ns }),
  });
  if (!response.ok) throw new Error(`memory/delete summaries: ${response.status}`);
  return await response.json();
}

export async function trimMemoryHistory(keepLastN, namespace = "normal") {
  const n = Number.isFinite(Number(keepLastN)) ? parseInt(keepLastN, 10) : 0;
  const ns = normalizeMemoryNamespace(namespace);
  const response = await apiFetch(`/memory/delete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ target: "history", keep_last_n: Math.max(0, n), namespace: ns }),
  });
  if (!response.ok) throw new Error(`memory/delete history: ${response.status}`);
  return await response.json();
}

export async function forgetMe() {
  const response = await apiFetch(`/forget-me`, { method: "POST" });
  if (!response.ok) throw new Error(`forget-me: ${response.status}`);
  return await response.json();
}

export async function setConsent(consent) {
  const response = await apiFetch(`/consent`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ consent: !!consent }),
  });
  if (!response.ok) throw new Error(`consent: ${response.status}`);
  return await response.json();
}

export async function getMemoryList(namespace = "normal") {
  console.log("[DEBUG-MEMORY] Chamando /memory/list");
  console.log("[DEBUG-MEMORY] session vh_user_id:", getBoundUserId());
  
  const response = await apiFetch(`/memory/list${memoryNsQuery(namespace)}`, { method: "GET" });
  
  console.log("[DEBUG-MEMORY] Status da resposta:", response.status);
  console.log("[DEBUG-MEMORY] Header X-VH-User-ID na resposta:", response.headers.get("X-VH-User-ID"));
  
  if (!response.ok) throw new Error(`memory/list: ${response.status}`);
  
  const data = await response.json();
  console.log("[DEBUG-MEMORY] Total de memórias recebidas:", data.items?.length || 0);
  
  return data;
}

export async function deleteMemoryItem(index, namespace = "normal") {
  const ns = normalizeMemoryNamespace(namespace);
  const response = await apiFetch(`/memory/delete_item`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ index: Number(index), namespace: ns }),
  });
  if (!response.ok) throw new Error(`memory/delete_item: ${response.status}`);
  return await response.json();
}

/**
 * Cartão de Identidade. Retorna { enabled, card, schema_version } em 200, ou null em 404/501/erro.
 * NUNCA lança erro — para capability detection segura.
 */
export async function getIdentityCard(namespace = "normal") {
  try {
    const response = await apiFetch(`/memory/identity_card${memoryNsQuery(namespace)}`, { method: "GET" });
    if (response.status === 404 || response.status === 501) return null;
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * Salva o Cartão de Identidade. Só chamar quando getIdentityCard retornou objeto.
 * Retorna { enabled, card, schema_version } (mesmo formato do GET).
 */
export async function postIdentityCard(card, namespace = "normal") {
  const ns = normalizeMemoryNamespace(namespace);
  const response = await apiFetch(`/memory/identity_card`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ card: card || {}, namespace: ns }),
  });
  if (!response.ok) throw new Error(`identity_card: ${response.status}`);
  return await response.json();
}

/**
 * Apaga o Cartão de Identidade (DELETE). Alternativa: postIdentityCard({}).
 */
export async function deleteIdentityCard(namespace = "normal") {
  const response = await apiFetch(`/memory/identity_card${memoryNsQuery(namespace)}`, { method: "DELETE" });
  if (!response.ok) throw new Error(`identity_card delete: ${response.status}`);
  return await response.json();
}

/**
 * GDPR: Solicitação de cópia ou exclusão de dados pessoais (Art. 15, 17).
 * Resposta em até 30 dias.
 */
export async function submitDataRequest(type, email) {
  const t = String(type || "").toLowerCase();
  if (t !== "copy" && t !== "deletion") throw new Error("type must be 'copy' or 'deletion'");
  const response = await apiFetch(`/data-request`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      type: t,
      email: String(email || "").trim().toLowerCase(),
    }),
  });
  if (!response.ok) throw new Error(`data-request: ${response.status}`);
  return await response.json();
}
